import type { VehicleReference } from "./vehicle.types";
import type { AdjacentEvents } from "@/features/bookings/components/adjacent-vehicle-events-accordion/utils/get-adjacent-events";
import type { ClientReference } from "@/types/client.types";
import type { Period } from "@/types/time.types";
import type { UserReference } from "@/types/user.types";
import {
	type BookingStatusDto,
	type BookingType,
	VehicleUsageStatusDto,
} from "@/lib/api";

export type VehicleUsageStatus = VehicleUsageStatusDto;
export const VehicleUsageStatus = VehicleUsageStatusDto;

/**
 * How a vehicle usage is classified for the "Type" column/chip:
 * - {@link VEHICLE_USAGE_TYPE.BOOKING}: backed by a real (non-SYNTHETIC) booking,
 * - {@link VEHICLE_USAGE_TYPE.PRIVATE_USAGE}: no booking, or a SYNTHETIC (private-use) booking,
 * - {@link VEHICLE_USAGE_TYPE.UNKNOWN}: a resolved booking whose type is UNKNOWN/absent.
 */
export const VEHICLE_USAGE_TYPE = {
	BOOKING: "BOOKING",
	PRIVATE_USAGE: "PRIVATE_USAGE",
	UNKNOWN: "UNKNOWN",
} as const;
export type VehicleUsageType =
	(typeof VEHICLE_USAGE_TYPE)[keyof typeof VEHICLE_USAGE_TYPE];

/** Overdue rows are always classifiable from their booking — never UNKNOWN. */
export type OverdueUsageType = Exclude<VehicleUsageType, "UNKNOWN">;

/** Identity + timing shared by every vehicle-usage shape. */
interface VehicleUsageBase {
	id?: string;
	/** Booking to navigate to (booking.id, falling back to vehicleUsage.bookingId). */
	bookingId?: string;
	period?: Period;
	status?: VehicleUsageStatus;
}

/** Odometer readings carried by the usage shapes. */
interface UsageMileage {
	mileageStartValue?: number;
	mileageEndValue?: number;
	mileageDelta?: number;
}

/** Vehicle / driver / client references needed to render a usage as a table row. */
interface VehicleUsageRowRefs {
	/** Vehicle the usage runs on — needed to end the usage from the list. */
	vehicleId?: string;
	vehicle?: VehicleReference;
	driver?: UserReference;
	client?: ClientReference;
}

/** The core of a single vehicle usage, independent of how it is listed. */
interface VehicleUsageCore extends VehicleUsageBase, UsageMileage {
	statusMessage?: string;
	isOverdue: boolean;
}

/** Single-vehicle, usage-only view (no row references). */
export type ActiveVehicleUsage = VehicleUsageCore;

/**
 * A row in the "Active Vehicle Usages" overview table. Unlike
 * {@link ActiveVehicleUsage} (usage-only), this carries the vehicle / driver /
 * client references needed to render the list.
 */
export interface ActiveVehicleUsageListItem
	extends VehicleUsageCore,
		VehicleUsageRowRefs {
	/** Booking vs. private usage (SYNTHETIC bookings count as private). */
	usageType: VehicleUsageType;
	/** The booking's planned period, when this usage stems from a booking. */
	plannedPeriod?: Period;
	/** Minutes elapsed since the planned end, when overdue. */
	overdueByMinutes?: number;
}

/**
 * A row in the "Overdue Vehicle Usages" overview table. Backed by the single
 * `GET /fleet/vehicle-usage/v1/overdue` endpoint, it carries the usage plus the
 * vehicle / driver / booking references and the telemetry + adjacent-event
 * enrichment the table renders.
 */
export interface OverdueVehicleUsage
	extends VehicleUsageBase,
		VehicleUsageRowRefs {
	/** True when the usage has no booking at all (private/owner-driven) — those rows are not clickable. */
	isPrivateUsage: boolean;
	/** Type chip classification: BOOKING, or PRIVATE_USAGE (no booking or SYNTHETIC). */
	usageType: OverdueUsageType;
	/** Planned end driving the overdue duration: booking planned end, else usage period end. */
	plannedEnd?: string;
	batteryPercentage?: number;
	distanceFromHomePosition?: number;
	adjacentEvents?: AdjacentEvents;
}

/**
 * A row in a vehicle's usage *history* (the paged
 * `GET /fleet/vehicle-usage/v1/vehicle/{vehicleId}` endpoint). Unlike
 * {@link ActiveVehicleUsageListItem} this is not concerned with the live lock
 * status — it surfaces the booking classification (type/status) and whether the
 * usage was private (no booking).
 */
export interface VehicleUsageHistoryItem extends UsageMileage {
	id?: string;
	/** Booking to navigate to; `undefined` for private/owner-driven usages. */
	bookingId?: string;
	isPrivate: boolean;
	/** Type chip classification, shared with the active-usages table. */
	usageType: VehicleUsageType;
	driver?: UserReference;
	client?: ClientReference;
	bookingStatus?: BookingStatusDto;
	period?: Period;
}

/** Server-side filters for a vehicle's usage history. */
export interface VehicleUsageHistoryFilters {
	period?: Period;
	bookingTypes?: Array<BookingType>;
	userRemoteId?: string;
}
